import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

const API = process.env.REACT_APP_API_URL;

// Localhost and the dev.* frontend use the development mobile app. All other
// frontend hosts use the production app, so no separate environment variable is
// needed for the deep-link scheme.
const FRONTEND_HOST =
  typeof window === "undefined" ? "" : window.location.hostname.toLowerCase();
const IS_DEVELOPMENT_FRONTEND =
  FRONTEND_HOST === "localhost" ||
  FRONTEND_HOST === "127.0.0.1" ||
  FRONTEND_HOST.startsWith("dev.");
const APP_SCHEME = IS_DEVELOPMENT_FRONTEND ? "myapp-dev" : "myapp";
const IOS_STORE_URL =
  process.env.REACT_APP_IOS_APP_STORE_URL || "https://apps.apple.com/de/search?term=SteffiCare";
const ANDROID_STORE_URL =
  process.env.REACT_APP_ANDROID_PLAY_STORE_URL || "https://play.google.com/store/search?q=SteffiCare&c=apps";

function detectPlatform() {
  if (typeof navigator === "undefined") return "desktop";
  const ua = navigator.userAgent || "";
  if (/android/i.test(ua)) return "android";
  if (/iPad|iPhone|iPod/.test(ua)) return "ios";
  if (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1) return "ios";
  return "desktop";
}

export default function FamilyInviteAcceptPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const platform = useMemo(() => detectPlatform(), []);
  const isMobile = platform !== "desktop";
  const appHref = useMemo(() => `${APP_SCHEME}://family/invite/${token}`, [token]);
  const storeHref = platform === "ios" ? IOS_STORE_URL : ANDROID_STORE_URL;

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [invite, setInvite] = useState(null);
  const [err, setErr] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [relationshipLabel, setRelationshipLabel] = useState("");
  const [hasAttemptedAppOpen, setHasAttemptedAppOpen] = useState(false);
  const [showBrowserFlow, setShowBrowserFlow] = useState(platform === "desktop");

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await fetch(`${API}/family-invites/redeem/${token}`, {
          credentials: "include",
        });
        const data = await res.json().catch(() => ({}));
        if (!alive) return;
        if (!res.ok) throw new Error(data?.message || "Einladung ist ungueltig oder abgelaufen.");
        setInvite(data);
      } catch (error) {
        if (!alive) return;
        setErr(error?.message || "Einladung ist ungueltig oder abgelaufen.");
      } finally {
        if (!alive) return;
        setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [token]);

  useEffect(() => {
    if (loading || !invite || !isMobile || hasAttemptedAppOpen) return undefined;
    setHasAttemptedAppOpen(true);
    const timer = window.setTimeout(() => {
      setShowBrowserFlow(true);
    }, 1400);
    window.location.assign(appHref);
    return () => window.clearTimeout(timer);
  }, [appHref, hasAttemptedAppOpen, invite, isMobile, loading]);

  async function handleSubmit(e) {
    e.preventDefault();
    setErr("");
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/family-invites/redeem/${token}`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email,
          password,
          relationship_label: relationshipLabel,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.message || "Einladung konnte nicht eingelöst werden.");
      navigate("/family", { replace: true });
    } catch (error) {
      setErr(error?.message || "Einladung konnte nicht eingelöst werden.");
    } finally {
      setSubmitting(false);
    }
  }

  function handleOpenApp() {
    window.location.assign(appHref);
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#DEF1FF] px-4 py-8">
      <div className="w-full max-w-lg rounded-3xl border border-black/10 bg-white/80 p-8 shadow-sm backdrop-blur">
        <p className="text-sm font-medium text-blue-700">SteffiCare Familie</p>
        <h1 className="mt-2 text-3xl font-semibold text-gray-900">Einladung annehmen</h1>

        {loading ? (
          <p className="mt-4 text-sm text-gray-600">Einladung wird geprueft...</p>
        ) : null}

        {!loading && invite && isMobile ? (
          <div className="mt-5 rounded-3xl border border-blue-100 bg-blue-50/80 px-5 py-5">
            <p className="text-sm font-semibold text-gray-900">
              Einladung fuer {invite.patient?.forename} {invite.patient?.surname}
            </p>
            <p className="mt-2 text-sm text-gray-600">
              Wir versuchen, SteffiCare direkt auf deinem Telefon zu oeffnen.
            </p>
            <div className="mt-4 space-y-3">
              <button
                type="button"
                onClick={handleOpenApp}
                className="w-full rounded-full bg-blue-600 px-5 py-3 text-sm font-medium text-white hover:bg-blue-700"
              >
                In SteffiCare oeffnen
              </button>
              <a
                href={storeHref}
                target="_blank"
                rel="noreferrer"
                className="block w-full rounded-full border border-gray-200 bg-white px-5 py-3 text-center text-sm font-medium text-gray-900 hover:bg-gray-50"
              >
                {platform === "ios" ? "Im App Store oeffnen" : "Bei Google Play oeffnen"}
              </a>
              <button
                type="button"
                onClick={() => setShowBrowserFlow(true)}
                className="w-full text-sm font-medium text-blue-700 hover:text-blue-800"
              >
                Im Browser fortfahren
              </button>
            </div>
          </div>
        ) : null}

        {!loading && invite && showBrowserFlow ? (
          <>
            <p className="mt-5 text-sm text-gray-600">
              Dieses Familienkonto wird mit{" "}
              <span className="font-semibold text-gray-900">
                {invite.patient?.forename} {invite.patient?.surname}
              </span>{" "}
              verknuepft.
            </p>
            <form onSubmit={handleSubmit} className="mt-6 space-y-4">
              <input
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                placeholder="Email-Adresse"
                className="w-full rounded-full border border-gray-200 bg-white px-5 py-3 text-sm text-gray-900 outline-none focus:border-blue-300 focus:ring-2 focus:ring-blue-100"
              />
              <input
                type="password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                placeholder="Passwort"
                className="w-full rounded-full border border-gray-200 bg-white px-5 py-3 text-sm text-gray-900 outline-none focus:border-blue-300 focus:ring-2 focus:ring-blue-100"
              />
              <input
                type="text"
                value={relationshipLabel}
                onChange={(event) => setRelationshipLabel(event.target.value)}
                placeholder="Beziehung, z. B. Tochter"
                className="w-full rounded-full border border-gray-200 bg-white px-5 py-3 text-sm text-gray-900 outline-none focus:border-blue-300 focus:ring-2 focus:ring-blue-100"
              />

              {err ? (
                <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                  {err}
                </div>
              ) : null}

              <button
                type="submit"
                disabled={submitting}
                className="w-full rounded-full bg-blue-600 px-5 py-3 text-sm font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-blue-300"
              >
                {submitting ? "Familienkonto wird erstellt..." : "Familienkonto erstellen"}
              </button>
            </form>
          </>
        ) : null}

        {!loading && !invite ? (
          <div className="mt-5 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {err || "Einladung ist ungueltig oder abgelaufen."}
          </div>
        ) : null}

        <div className="mt-6">
          <Link to="/family/login" className="text-sm font-medium text-blue-700 hover:text-blue-800">
            Bereits registriert? Zum Familien-Login
          </Link>
        </div>
      </div>
    </div>
  );
}
